import { useEffect, useState } from "react";
import { Common, CustomScrollbar } from "@/components";
import { useImportOrder } from "../providers/ImportOrderContext";
import { useGetDriverList } from "../services/driverService";
import { useNavigate } from "react-router-dom";
import { DriverListBox } from "./DriverListBox";
import { NewRoute, RouteDriverSelection } from "../models";
import { useAddRoutes } from "../services/salesOrdersService";
import { SnackbarUtils } from "@/utils/notistack";
import InviteDriversDialog from "./InviteDriversDialog";

const FixRoutes = () => {
  const { state, dispatch } = useImportOrder();
  const navigate = useNavigate();
  const {
    data: driversResponse,
    isFetching: isFetchingDrivers,
    refetch: refetchDrivers,
  } = useGetDriverList();
  const [routeDrivers, setRouteDrivers] = useState<RouteDriverSelection[]>([]);
  const addRoutesMutation = useAddRoutes();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Initialize route drivers when component mounts or invalidRoutes changes
  useEffect(() => {
    if (state.invalidRoutes) {
      setRouteDrivers(
        state.invalidRoutes.map((route) => {
          return {
            routeName: route.routeName,
            defaultDriverId: "",
            orderCount: route.docNumbers.length,
          };
        })
      );
    }
  }, [state.invalidRoutes]);

  // Check if all routes have drivers assigned
  const allRoutesHaveDrivers = routeDrivers.every(
    (route) => route.defaultDriverId
  );

  const handleDriverChange = (routeName: string, driverId: string) => {
    const newRouteDrivers = routeDrivers.map((route) =>
      route.routeName === routeName
        ? { ...route, defaultDriverId: driverId }
        : route
    );
    setRouteDrivers(newRouteDrivers);
  };

  const handleConfirm = async () => {
    const newRoutes: NewRoute[] = routeDrivers.map((route) => ({
      routeName: route.routeName,
      defaultDriverId: route.defaultDriverId,
    }));

    const addRoutesResult = await addRoutesMutation.mutateAsync({
      importSessionId: state.importSessionId ?? "",
      newRoutes,
    });

    if (!addRoutesResult.success || addRoutesResult.errors.length > 0) {
      addRoutesResult.errors.forEach((error) => {
        SnackbarUtils.error(error);
      });
      return;
    }

    const invalidAddressesDocNumbers = Array.from(
      new Set(
        state.invalidAddresses
          .map((invalidAddress) => invalidAddress.docNumbers)
          .flat()
      )
    );

    dispatch({
      type: "SET_INVALID_ORDER_COUNT",
      payload: invalidAddressesDocNumbers.length,
    });

    dispatch({
      type: "SET_INVALID_ROUTES",
      payload: [],
    });

    navigate("#review");
  };

  const renderDriverSelector = (route: RouteDriverSelection) => {
    return (
      <DriverListBox
        drivers={driversResponse?.data || []}
        selectedDriverId={route.defaultDriverId}
        onChange={(driverId) => handleDriverChange(route.routeName, driverId)}
        onRefresh={() => {
          refetchDrivers();
        }}
        loading={isFetchingDrivers}
        importSessionId={state.importSessionId ?? ""}
      />
    );
  };

  return (
    <div className="relative">
      <InviteDriversDialog
        open={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        importSessionId={state.importSessionId ?? ""}
        onSuccess={refetchDrivers}
      />
      <div className="h-[calc(100vh-258px)]">
        <h3 className="mb-4 text-hd5">
          Below route names are not found in TOM system. Select the routes that
          you would like to create as new routes and set default drivers.
        </h3>
        <div className="mb-6 text-neutral-50">
          Would you like to invite new drivers?
          <button
            onClick={() => setIsInviteModalOpen(true)}
            className="ml-2 text-blue hover:text-blue-dark underline underline-offset-2"
          >
            Click here
          </button>
        </div>

        <div className="h-[calc(100%-80px)]">
          <CustomScrollbar>
            <div className="relative w-full border border-line border-separate border-spacing-0">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left">
                      Route Name
                    </th>
                    <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-center">
                      Number of Orders
                    </th>
                    <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left w-[600px]">
                      Default Driver
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {routeDrivers.length > 0 ? (
                    routeDrivers.map((route) => (
                      <tr key={route.routeName} className="hover:bg-neutral-5">
                        <td className="px-4 py-2 border-b border-neutral-10">
                          {route.routeName}
                        </td>
                        <td className="px-4 py-2 border-b border-neutral-10 text-center">
                          {route.orderCount}
                        </td>
                        <td className="px-4 py-2 border-b border-neutral-10 w-[600px]">
                          {renderDriverSelector(route)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={3}
                        className="p-4 text-center text-neutral-50"
                      >
                        No routes to fix
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CustomScrollbar>
        </div>
      </div>

      <div className="flex justify-end space-x-3 mt-5">
        <Common.Button color="transparent" onClick={() => navigate("#review")}>
          Cancel
        </Common.Button>
        <Common.Button
          onClick={handleConfirm}
          disabled={!allRoutesHaveDrivers || addRoutesMutation.isLoading}
          isLoading={addRoutesMutation.isLoading}
        >
          {addRoutesMutation.isLoading ? "Adding..." : "Confirm"}
        </Common.Button>
      </div>
    </div>
  );
};

export default FixRoutes;
